import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import '../styles/about.css';
import {
  FaLeaf, FaLightbulb, FaShieldAlt, FaUsers,
  FaHandshake, FaGavel, FaTruck, FaLock, FaCogs, FaRecycle,
  FaChevronRight, FaEye, FaBullseye, FaQuoteLeft, FaArrowRight,
  FaCheckCircle
} from 'react-icons/fa';

/* ─── Framer variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      className={className}
      variants={stagger}
      initial="hidden"
      animate={inView ? 'show' : 'hidden'}
    >
      {children}
    </motion.section>
  );
}

const commitments = [
  { icon: <FaLeaf />,      title: 'Sustainability',   p1: 'Environmentally friendly processes that minimise waste.',         p2: 'Sustainable practices in every aspect of our operations.' },
  { icon: <FaLightbulb />, title: 'Innovation',       p1: 'Continuously improving methods through cutting-edge technology.', p2: 'Staying ahead of industry trends for the best solutions.' },
  { icon: <FaShieldAlt />, title: 'Security',         p1: 'Secure destruction of data and sensitive information.',           p2: 'Highest data security standards throughout every process.' },
  { icon: <FaUsers />,     title: 'Customer Focus',   p1: 'Exceptional service and customised solutions per client.',        p2: 'Long-term relationships built on trust and transparency.' },
  { icon: <FaHandshake />, title: 'Community Impact', p1: 'Responsible recycling and environmental stewardship.',            p2: 'Educating communities on the importance of e-waste recycling.' },
  { icon: <FaGavel />,     title: 'Compliance',       p1: 'Adhering to all local, national, and international regulations.', p2: 'Legally and ethically sound operations at all times.' },
];

const steps = [
  { img: '/Assets/kmkimg8.jpg',  icon: <FaTruck />,   title: 'Collection', desc: 'We arrange pickup of e-waste and metal scrap directly from your location.' },
  { img: '/Assets/kmkimg14.jpg', icon: <FaLock />,    title: 'Security',   desc: 'Safe, secure transportation by our dedicated logistics team.' },
  { img: '/Assets/kmkimg16.jpg', icon: <FaCogs />,    title: 'Processing', desc: 'Materials sorted by type and condition at our certified facility.' },
  { img: '/Assets/imgkmk13.jpg', icon: <FaRecycle />, title: 'Recycling',  desc: 'Eco-friendly recycling of all non-reusable e-waste and metals.' },
];

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="about-page">

      {/* ── Hero ── */}
      <section className="about-hero">
        <span className="ah-overlay" />
        <span className="ah-glow-a" />
        <span className="ah-glow-b" />
        <div className="ah-particles" aria-hidden="true">
          {[...Array(5)].map((_, i) => <span key={i} className={`ah-part ah-part--${i+1}`} />)}
        </div>

        <div className="container ah-inner">
          <motion.div
            className="ah-content"
            initial="hidden"
            animate="show"
            variants={stagger}
          >
            <motion.nav className="ah-breadcrumb" variants={fadeUp}>
              <Link to="/">Home</Link>
              <FaChevronRight className="ah-sep" />
              <span>About</span>
            </motion.nav>
            <motion.p className="ah-eyebrow" variants={fadeUp}>Who We Are</motion.p>
            <motion.h1 className="ah-title" variants={fadeUp}>
              About <span className="ah-title-accent">KMK Industries</span>
            </motion.h1>
            <motion.p className="ah-subtitle" variants={fadeUp}>
              Specialists in comprehensive e-waste recycling and metal scrap management —
              delivering environmentally responsible solutions for businesses across India.
            </motion.p>
            <motion.div className="ah-hero-actions" variants={fadeUp}>
              <button className="ah-btn-primary" onClick={() => navigate('/contact')}>
                Get In Touch <FaArrowRight />
              </button>
              <button className="ah-btn-outline" onClick={() => navigate('/services')}>
                Our Services
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="ah-scroll-hint" aria-hidden="true">
          <span className="ah-scroll-dot" />
        </div>
      </section>

      {/* ── Welcome / Vision & Mission ── */}
      <Section className="about-welcome">
        <div className="container aw-inner">
          <div className="aw-grid">

            <motion.div className="aw-media" variants={fadeLeft}>
              <span className="aw-frame" />
              <img src="/Assets/kmkimg5.jpg" alt="KMK Industries facility" className="aw-img" />
              <motion.div
                className="aw-media-badge"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, type: 'spring', stiffness: 120 }}
              >
                <FaRecycle className="aw-badge-icon" />
                <div>
                  <strong>ISO Certified</strong>
                  <p>Eco Recycling</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="aw-content" variants={fadeRight}>
              <span className="aw-eyebrow">Welcome to KMK Industries</span>
              <h2 className="aw-title">Responsible Recycling,<br />Built on Expertise</h2>
              <p className="aw-text">
                At KMK Industries, we specialise in comprehensive e-waste recycling management —
                offering environmentally responsible solutions for disposing of electronic waste
                and metal scrap. Our mission is to protect the planet through innovative recycling
                techniques and sustainable practices.
              </p>
              <blockquote className="aw-quote">
                <FaQuoteLeft className="aw-quote-icon" />
                <p>
                  We are dedicated to providing reliable, responsible, and sustainable recycling
                  solutions. Choose KMK and join us in making a positive impact on the environment.
                </p>
              </blockquote>
              <div className="vm-grid">
                {[
                  { icon: <FaEye />,      title: 'Our Vision',  text: 'To lead the way in sustainable recycling globally — creating a cleaner, healthier planet for future generations through continuous innovation.' },
                  { icon: <FaBullseye />, title: 'Our Mission', text: 'To provide comprehensive recycling solutions that protect the environment, support communities, and deliver maximum value to every client.' },
                ].map((card, i) => (
                  <motion.article
                    className="vm-card"
                    key={i}
                    variants={fadeUp}
                    whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  >
                    <span className="vm-icon">{card.icon}</span>
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                  </motion.article>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </Section>

      {/* ── Core values ── */}
      <Section className="about-values">
        <div className="container av-inner">
          <motion.header className="av-head" variants={fadeUp}>
            <span className="av-eyebrow">Our Core Values</span>
            <h2 className="av-title">We Are Committed To</h2>
            <p className="av-desc">
              Six principles that shape every pickup, every process and every partnership.
            </p>
          </motion.header>

          <div className="av-grid">
            {commitments.map((c, i) => (
              <motion.article
                className="value-card"
                key={c.title}
                variants={fadeUp}
                custom={i}
                whileHover={{ y: -8, transition: { duration: 0.25 } }}
              >
                <span className="value-icon">{c.icon}</span>
                <h3 className="value-title">{c.title}</h3>
                <p>{c.p1}</p>
                <p>{c.p2}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Recycling process ── */}
      <Section className="about-process">
        <div className="container">

          <div className="ap-head">
            <motion.div className="ap-head-text" variants={fadeLeft}>
              <span className="ap-eyebrow">Recycling Wastage · Save Environment</span>
              <h2 className="ap-title">Simple Steps: Waste to<br />Recycled Resource</h2>
              <p className="ap-desc">
                Recycling converts waste into new materials using modern machinery and skilled staff.
                Our step-by-step process ensures every item is handled responsibly from collection
                to final processing.
              </p>
              <ul className="ap-check-list">
                {['Certified facility & trained staff','Zero data compromise','Eco-certified disposal methods','Full documentation provided'].map((t,i) => (
                  <li key={i}><FaCheckCircle /> {t}</li>
                ))}
              </ul>
              <button className="ah-btn-primary" onClick={() => navigate('/contact')}>
                Get Started <FaArrowRight />
              </button>
            </motion.div>
            <motion.div className="ap-head-media" variants={fadeRight}>
              <img src="/Assets/kmkimg10.jpg" alt="Recycling process at KMK Industries" />
              <div className="ap-img-glow" />
            </motion.div>
          </div>

          <ol className="ap-steps">
            {steps.map((s, i) => (
              <motion.li
                className="step-card"
                key={s.title}
                variants={fadeUp}
                custom={i}
                whileHover={{ y: -8, transition: { duration: 0.25 } }}
              >
                <span className="step-num">{String(i + 1).padStart(2, '0')}</span>
                <div className="step-media">
                  <img src={s.img} alt={s.title} />
                </div>
                <div className="step-body">
                  <h4 className="step-title">
                    <span className="step-title-icon">{s.icon}</span>
                    {s.title}
                  </h4>
                  <p className="step-desc">{s.desc}</p>
                </div>
              </motion.li>
            ))}
          </ol>

        </div>
      </Section>

      {/* ── CTA banner ── */}
      <section className="about-cta">
        <div className="about-cta-grid-lines" aria-hidden="true" />
        <div className="about-cta-glow" aria-hidden="true" />
        <div className="container">
          <motion.div
            className="about-cta-inner"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.65 }}
          >
            <span className="about-cta-icon"><FaRecycle /></span>
            <h2>Ready to Recycle <span className="text-green-light">Responsibly?</span></h2>
            <p>Join 500+ businesses that trust KMK Industries for certified e-waste and metal recycling.</p>
            <div className="about-cta-btns">
              <button className="ah-btn-primary" onClick={() => navigate('/contact')}>
                Get Free Quote <FaArrowRight />
              </button>
              <button className="ah-btn-outline" onClick={() => navigate('/galary')}>
                View Our Work
              </button>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default About;
